import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export type TimebankKind = "deposit" | "withdrawal";
// VIB-726: "cancelled" settes når admin angrer en godkjent/avslått transaksjon
// (samtidig med soft-delete via deleted_at). Synlig som egen status-filter.
export type TimebankStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface TimebankTransaction {
  id: string;
  user_id: string;
  kind: TimebankKind;
  hours: number;
  status: TimebankStatus;
  assignment_id: string | null;
  time_entry_id: string | null;
  start_date: string | null;
  end_date: string | null;
  /** VIB-1286: valgfrie klokkeslett på uttak — kun satt for withdrawal. */
  start_time: string | null;
  end_time: string | null;
  comment: string | null;
  rejected_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  user?: { full_name: string; email: string } | null;
  decider?: { full_name: string } | null;
  assignment?: {
    budget_line_id: string;
    budget_lines?: {
      description: string | null;
      project_id: string;
      projects?: { name: string | null } | null;
    } | null;
  } | null;
  /** VIB-1286: for oppsparing (deposit) — timeføringens dato, dvs. når
   * timene faktisk ble opparbeidet (kan avvike fra created_at/søkt-dato). */
  time_entry?: { start_date: string | null } | null;
}

const SELECT_FRAGMENT =
  "*, user:user_id(full_name, email), decider:decided_by(full_name), assignment:assignment_id(budget_line_id, budget_lines(description, project_id, projects(name))), time_entry:time_entry_id(start_date)";

/** Henter alle transaksjoner for én bruker, nyeste først.
 *  VIB-726: Ekskluderer cancelled (soft-deleted) rader — de teller ikke i saldo
 *  og skal ikke vises som aktiv historikk. Admin ser dem via egen filter.
 */
export function useTimebankTransactions(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["timebank-transactions", userId],
    enabled: !!userId,
    queryFn: async (): Promise<TimebankTransaction[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("timebank_transactions")
        .select(SELECT_FRAGMENT)
        .eq("user_id", userId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as TimebankTransaction[];
    },
  });
}

/**
 * Beregner brukerens netto saldo: SUM(approved deposit) - SUM(approved withdrawal).
 * Pending-transaksjoner regnes ikke inn i saldoen, men returneres separat
 * for å vise "X timer venter godkjenning".
 */
export function useTimebankBalance(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["timebank-balance", userId],
    enabled: !!userId,
    queryFn: async () => {
      if (!userId) return { balance: 0, pendingDeposit: 0, pendingWithdrawal: 0 };
      // VIB-726: ekskluder cancelled — soft-deletede approved teller ikke
      // lenger i saldo, og en pending som ble cancelled skal heller ikke
      // dukke opp som "venter godkjenning".
      const { data, error } = await supabase
        .from("timebank_transactions")
        .select("kind, hours, status")
        .eq("user_id", userId)
        .is("deleted_at", null);
      if (error) throw error;
      let balance = 0;
      let pendingDeposit = 0;
      let pendingWithdrawal = 0;
      for (const t of (data ?? []) as Array<Pick<TimebankTransaction, "kind" | "hours" | "status">>) {
        if (t.status === "approved") {
          balance += t.kind === "deposit" ? Number(t.hours) : -Number(t.hours);
        } else if (t.status === "pending") {
          if (t.kind === "deposit") pendingDeposit += Number(t.hours);
          else pendingWithdrawal += Number(t.hours);
        }
      }
      return { balance, pendingDeposit, pendingWithdrawal };
    },
  });
}

/** Alle pending-transaksjoner på tvers av brukere — for daglig leder. */
export function usePendingTimebankRequests() {
  return useQuery({
    queryKey: ["timebank-pending"],
    queryFn: async (): Promise<TimebankTransaction[]> => {
      const { data, error } = await supabase
        .from("timebank_transactions")
        .select(SELECT_FRAGMENT)
        .eq("status", "pending")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TimebankTransaction[];
    },
  });
}

// VIB-726: Filtre for admin-modus "Alle transaksjoner"-tabellen.
export interface TimebankFilters {
  userId?: string | null;
  kind?: TimebankKind | null;
  status?: TimebankStatus | null;
  fromDate?: string | null;
  toDate?: string | null;
  page?: number;
  pageSize?: number;
}

/** Admin-modus: alle transaksjoner på tvers av brukere med filtre + paginering.
 *  Inkluderer cancelled-rader (synlige i egen status-filter).
 */
export function useAllTimebankTransactions(filters: TimebankFilters) {
  return useQuery({
    queryKey: ["timebank-all", filters],
    queryFn: async (): Promise<{ rows: TimebankTransaction[]; total: number }> => {
      let q = supabase
        .from("timebank_transactions")
        .select(SELECT_FRAGMENT, { count: "exact" })
        .order("created_at", { ascending: false });

      if (filters.userId) q = q.eq("user_id", filters.userId);
      if (filters.kind) q = q.eq("kind", filters.kind);
      if (filters.status) q = q.eq("status", filters.status);
      if (filters.fromDate) q = q.gte("created_at", filters.fromDate);
      if (filters.toDate) q = q.lte("created_at", filters.toDate);

      const page = filters.page ?? 0;
      const size = filters.pageSize ?? 50;
      q = q.range(page * size, (page + 1) * size - 1);

      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as TimebankTransaction[], total: count ?? 0 };
    },
  });
}

export function useRequestTimebankDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      user_id: string;
      assignment_id: string | null;
      time_entry_id: string;
      hours: number;
      comment?: string;
      task_description?: string;
    }) => {
      const { data, error } = await supabase
        .from("timebank_transactions")
        .insert({
          user_id: input.user_id,
          kind: "deposit",
          hours: input.hours,
          assignment_id: input.assignment_id,
          time_entry_id: input.time_entry_id,
          comment: input.comment ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;
      // Varsel (timebank_requested) til admins opprettes av DB-trigger på insert.
      return data.id;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "timebank_transactions", { userId: vars.user_id });
      toast.success("Søknad sendt til godkjenning");
    },
    onError: (err) => {
      toast.error(`Kunne ikke sende søknad: ${(err as Error).message}`);
    },
  });
}

export function useRequestTimebankWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      user_id: string;
      hours: number;
      start_date: string;
      end_date: string;
      comment?: string;
      /** VIB-1286: valgfrie klokkeslett — skiller halv dag fra hel dag. */
      start_time?: string | null;
      end_time?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("timebank_transactions")
        .insert({
          user_id: input.user_id,
          kind: "withdrawal",
          hours: input.hours,
          start_date: input.start_date,
          end_date: input.end_date,
          start_time: input.start_time || null,
          end_time: input.end_time || null,
          comment: input.comment ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;
      // Varsel (timebank_requested) til admins opprettes av DB-trigger på insert.
      return data.id;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "timebank_transactions", { userId: vars.user_id });
      toast.success("Søknad om uttak sendt");
    },
    onError: (err) => {
      toast.error(`Kunne ikke sende søknad: ${(err as Error).message}`);
    },
  });
}

export function useDecideTimebankRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      transaction: TimebankTransaction;
      decision: "approved" | "rejected";
      decided_by: string;
      rejected_reason?: string;
    }) => {
      const { transaction, decision, decided_by, rejected_reason } = input;
      const { error } = await supabase
        .from("timebank_transactions")
        .update({
          status: decision,
          decided_by,
          decided_at: new Date().toISOString(),
          rejected_reason: decision === "rejected" ? rejected_reason ?? null : null,
        })
        .eq("id", transaction.id);
      if (error) throw error;
      // Varsel (timebank_approved/rejected) til søker opprettes av DB-trigger
      // på status-update.
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "timebank_transactions", {
        userId: vars.transaction.user_id,
      });
      toast.success(vars.decision === "approved" ? "Søknad godkjent" : "Søknad avslått");
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere søknaden: ${(err as Error).message}`);
    },
  });
}

export function useCancelTimebankRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; user_id: string }) => {
      const { error } = await supabase
        .from("timebank_transactions")
        .delete()
        .eq("id", input.id)
        .eq("status", "pending");
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "timebank_transactions", { userId: vars.user_id });
      toast.success("Søknad trukket");
    },
    onError: (err) => {
      toast.error(`Kunne ikke trekke søknad: ${(err as Error).message}`);
    },
  });
}

/** VIB-726: Admin cancellerer en godkjent/avslått timebank-transaksjon.
 *  Krever begrunnelse som sendes til berørt bruker som varsel.
 *  Pending-rader skal IKKE cancelleres her — bruk vanlig avslå-flyt i stedet.
 */
export function useCancelTimebankTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      transaction: TimebankTransaction;
      reason: string;
      decided_by: string;
    }) => {
      if (!input.reason.trim()) {
        throw new Error("Begrunnelse er påkrevd");
      }
      if (input.transaction.status === "pending") {
        throw new Error(
          "Pending-søknader avslås via vanlig flyt, ikke cancel-flyten",
        );
      }

      const { error } = await supabase
        .from("timebank_transactions")
        .update({
          status: "cancelled",
          deleted_at: new Date().toISOString(),
          deleted_by: input.decided_by,
          deleted_reason: input.reason.trim(),
        })
        .eq("id", input.transaction.id);
      if (error) throw error;
      // Varsel (timebank_cancelled) til søker opprettes av DB-trigger på
      // status-update til 'cancelled'.
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "timebank_transactions", {
        userId: vars.transaction.user_id,
      });
      toast.success("Transaksjon fjernet");
    },
    onError: (err) => {
      toast.error(`Kunne ikke fjerne: ${(err as Error).message}`);
    },
  });
}
